import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import VehicleCard from '@/components/VehicleCard';
import { Search, X, ChevronDown, ChevronUp, SlidersHorizontal } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import useSEO from '@/hooks/useSEO';

// Price filter buckets as specified
const PRICE_BUCKETS = [
  { label: 'Under $6,000', value: '0-6000', max: 6000 },
  { label: 'Under $10,000', value: '0-10000', max: 10000 },
  { label: 'Under $15,000', value: '0-15000', max: 15000 },
  { label: 'Under $20,000', value: '0-20000', max: 20000 },
  { label: 'Under $30,000', value: '0-30000', max: 30000 },
  { label: 'All Prices', value: 'all', max: null },
];

// Allowed features for filtering
const FEATURE_OPTIONS = [
  'Gas Saver',
  '4WD/AWD',
  'Backup Camera',
  'Apple CarPlay',
  'Android Auto',
  'Bluetooth Radio',
];

const InventoryPage = () => {
  useSEO('inventory', 'Inventory | We Have Your Car');
  const [vehicles, setVehicles] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [makes, setMakes] = useState([]);
  const [bodyTypes, setBodyTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    priceBucket: 'all',
    make: 'all',
    bodyType: 'all',
    features: [],
  });
  
  // Sort state - combined sortBy and sortOrder into single value
  const [sortOption, setSortOption] = useState('price-asc');

  useEffect(() => {
    fetchVehicles();
    fetchMeta();
  }, []);

  // Apply filters and sorting whenever they change
  useEffect(() => {
    applyFiltersAndSort();
  }, [filters, sortOption, allVehicles]);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vehicles?active_only=true`);
      setAllVehicles(response.data);
      
      // Extract unique body types from actual data
      const uniqueBodyTypes = [...new Set(
        response.data
          .map(v => v.body_type?.trim())
          .filter(bt => bt && bt.length > 0)
      )].sort();
      setBodyTypes(uniqueBodyTypes);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMeta = async () => {
    try {
      const response = await axios.get(`${API}/vehicles-meta`);
      setMakes(response.data.makes || []);
    } catch (error) {
      console.error('Error fetching meta:', error);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...allVehicles];
    
    // Check if any filter is active
    const hasActiveFilter = filters.priceBucket !== 'all' || 
                            filters.make !== 'all' || 
                            filters.bodyType !== 'all' ||
                            filters.features.length > 0;
    
    // Price filter
    if (filters.priceBucket !== 'all') {
      const bucket = PRICE_BUCKETS.find(b => b.value === filters.priceBucket);
      if (bucket && bucket.max) {
        filtered = filtered.filter(v => v.price <= bucket.max);
      }
    }
    
    // Make filter
    if (filters.make && filters.make !== 'all') {
      filtered = filtered.filter(v => v.make.toLowerCase() === filters.make.toLowerCase());
    }
    
    // Body type filter
    if (filters.bodyType && filters.bodyType !== 'all') {
      filtered = filtered.filter(v => v.body_type?.trim().toLowerCase() === filters.bodyType.toLowerCase());
    }
    
    // Features filter - check for 4WD or AWD when 4WD/AWD is selected, Gas Saver for 29+ MPG
    if (filters.features.length > 0) {
      filtered = filtered.filter(v => {
        return filters.features.every(feature => {
          if (feature === 'Gas Saver') {
            // Gas Saver = 29 MPG or more (city OR highway)
            return (v.mpg_city && v.mpg_city >= 29) || (v.mpg_highway && v.mpg_highway >= 29);
          }
          if (feature === '4WD/AWD') {
            // Check drivetrain OR features array
            return v.drivetrain === '4WD' || 
                   v.drivetrain === 'AWD' || 
                   v.features?.includes('4WD') || 
                   v.features?.includes('AWD');
          }
          return v.features?.includes(feature);
        });
      });
    }
    
    // Parse sort option
    const [sortBy, sortOrder] = sortOption.split('-');
    
    // Sort - featured vehicles only go first on initial load (default sort: price asc, no filters)
    const isDefaultSort = sortOption === 'price-asc';
    const shouldShowFeaturedFirst = !hasActiveFilter && isDefaultSort;
    
    if (shouldShowFeaturedFirst) {
      // Separate featured and non-featured
      const featured = filtered.filter(v => v.is_featured);
      const nonFeatured = filtered.filter(v => !v.is_featured);
      
      // Sort each group by price
      const sortGroup = (arr) => {
        return arr.sort((a, b) => a.price - b.price);
      };
      
      filtered = [...sortGroup(featured), ...sortGroup(nonFeatured)];
    } else {
      // Normal sort - user explicitly changed sort or has filters
      filtered.sort((a, b) => {
        let compareA = sortBy === 'price' ? a.price : a.year;
        let compareB = sortBy === 'price' ? b.price : b.year;
        
        if (sortOrder === 'asc') {
          return compareA - compareB;
        } else {
          return compareB - compareA;
        }
      });
    }
    
    setVehicles(filtered);
  };

  const handleFeatureToggle = (feature) => {
    setFilters(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const clearFilters = () => {
    setFilters({
      priceBucket: 'all',
      make: 'all',
      bodyType: 'all',
      features: [],
    });
    setSortOption('price-asc');
  };

  const hasActiveFilters = filters.priceBucket !== 'all' || 
                           filters.make !== 'all' || 
                           filters.bodyType !== 'all' ||
                           filters.features.length > 0;

  return (
    <div className="min-h-screen bg-[#0a1628] py-8">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 font-['Exo_2']">
            Our <span className="gradient-text">Inventory</span>
          </h1>
          <p className="text-[#a8b8d0]">
            Browse our selection of quality used vehicles
          </p>
        </div>

        {/* Filters Section */}
        <div className="bg-[#132238] rounded-xl p-4 mb-6 border border-[#1a2d47]">
          {/* Filters Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-wrap gap-3 items-center">
            {/* Price Filter */}
            <div>
              <Select 
                value={filters.priceBucket} 
                onValueChange={(v) => setFilters(prev => ({ ...prev, priceBucket: v }))}
              >
                <SelectTrigger className="bg-[#1a2d47] border-[#1a4d8c] text-white w-full" data-testid="price-filter">
                  <SelectValue placeholder="Price Range" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a2d47] border-[#1a4d8c]">
                  {PRICE_BUCKETS.map(bucket => (
                    <SelectItem key={bucket.value} value={bucket.value} className="text-white hover:bg-[#1a4d8c]">
                      {bucket.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Make Filter */}
            <div>
              <Select 
                value={filters.make} 
                onValueChange={(v) => setFilters(prev => ({ ...prev, make: v }))}
              >
                <SelectTrigger className="bg-[#1a2d47] border-[#1a4d8c] text-white w-full" data-testid="make-filter">
                  <SelectValue placeholder="All Makes" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a2d47] border-[#1a4d8c]">
                  <SelectItem value="all" className="text-white hover:bg-[#1a4d8c]">All Makes</SelectItem>
                  {makes.map(make => (
                    <SelectItem key={make} value={make} className="text-white hover:bg-[#1a4d8c]">{make}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Body Type Filter */}
            {bodyTypes.length > 0 && (
              <div>
                <Select 
                  value={filters.bodyType} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, bodyType: v }))}
                >
                  <SelectTrigger className="bg-[#1a2d47] border-[#1a4d8c] text-white w-full" data-testid="body-type-filter">
                    <SelectValue placeholder="Body Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a2d47] border-[#1a4d8c]">
                    <SelectItem value="all" className="text-white hover:bg-[#1a4d8c]">All Body Types</SelectItem>
                    {bodyTypes.map(type => (
                      <SelectItem key={type} value={type} className="text-white hover:bg-[#1a4d8c]">{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Sort Options */}
            <div>
              <Select value={sortOption} onValueChange={setSortOption}>
                <SelectTrigger className="bg-[#1a2d47] border-[#1a4d8c] text-white w-full" data-testid="sort-by">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a2d47] border-[#1a4d8c]">
                  <SelectItem value="price-asc" className="text-white hover:bg-[#1a4d8c]">Price Low to High</SelectItem>
                  <SelectItem value="price-desc" className="text-white hover:bg-[#1a4d8c]">Price High to Low</SelectItem>
                  <SelectItem value="year-asc" className="text-white hover:bg-[#1a4d8c]">Year Ascending</SelectItem>
                  <SelectItem value="year-desc" className="text-white hover:bg-[#1a4d8c]">Year Descending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Toggle Features Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors h-10 ${
                showFilters || filters.features.length > 0
                  ? 'bg-[#ff6b35] text-white'
                  : 'bg-[#1a2d47] text-[#a8b8d0] hover:text-white'
              }`}
              data-testid="toggle-features"
            >
              <SlidersHorizontal size={18} />
              Features
              {filters.features.length > 0 && (
                <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                  {filters.features.length}
                </span>
              )}
            </button>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-[#a8b8d0] hover:text-white transition-colors flex items-center justify-center gap-1 h-10"
                data-testid="clear-filters-btn"
              >
                <X size={16} />
                Clear
              </button>
            )}
          </div>

          {/* Features Checkboxes (expandable) */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-[#1a2d47]">
              <p className="text-[#a8b8d0] text-sm mb-3">Filter by Features:</p>
              <div className="flex flex-wrap gap-4">
                {FEATURE_OPTIONS.map(feature => (
                  <label 
                    key={feature} 
                    className="flex items-center gap-2 cursor-pointer text-white hover:text-[#ff6b35] transition-colors"
                  >
                    <Checkbox
                      checked={filters.features.includes(feature)}
                      onCheckedChange={() => handleFeatureToggle(feature)}
                      className="border-[#1a4d8c] data-[state=checked]:bg-[#ff6b35] data-[state=checked]:border-[#ff6b35]"
                    />
                    <span className="text-sm">{feature}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-6 flex justify-between items-center">
          <p className="text-[#a8b8d0]">
            Showing <span className="text-white font-medium">{vehicles.length}</span> vehicles
            {hasActiveFilters && <span className="text-[#ff6b35] ml-2">(filtered)</span>}
          </p>
        </div>

        {/* Vehicle Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-[#132238] rounded-xl h-80 animate-pulse"></div>
            ))}
          </div>
        ) : vehicles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="vehicles-grid">
            {vehicles.map((vehicle) => (
              <VehicleCard key={vehicle.id} vehicle={vehicle} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-[#132238] rounded-xl p-8 max-w-md mx-auto">
              <Search size={48} className="text-[#a8b8d0] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No vehicles found</h3>
              <p className="text-[#a8b8d0] mb-4">Try adjusting your filters or check back later for new arrivals.</p>
              <button onClick={clearFilters} className="btn-primary">
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryPage;
